<script lang="ts">
  import { CORP, HULL_CODE_LENGTH, NAME_MAX, PASSWORD_MIN, normalizeHullCode } from "@spacecrew/shared";
  import { board, commission, liveHulls, quickBoard, rememberedName, session } from "../net/session.svelte.js";
  import { DEV_HULL_CODE } from "@spacecrew/shared";
  import { onMount } from "svelte";
  import { audio } from "../audio/bus.js";
  import { BUILD_LABEL } from "../lib/build.js";
  import { gate } from "../net/gate.svelte.js";

  let name = $state(rememberedName());
  let password = $state("");
  let hullId = $state(normalizeHullCode(new URLSearchParams(location.search).get("hull") ?? ""));
  const busy = $derived(session.status === "joining");
  const valid = $derived(name.trim().length > 0 && password.length >= PASSWORD_MIN);

  const seedParam = new URLSearchParams(location.search).get("seed");
  function onCommission() { audio.unlock(); void commission(name, password, seedParam ? Number(seedParam) : undefined); }
  function onBoard() { audio.unlock(); void board(hullId, name, password); }
  function onQuick() { audio.unlock(); void quickBoard(name); }
  let hulls = $state<{ hull: string; crew: number }[]>([]);
  onMount(() => { const tick = async () => { hulls = await liveHulls(); }; void tick(); const t = setInterval(tick, 4000); return () => clearInterval(t); });
</script>

<section class="join" aria-labelledby="join-title">
  <h1 id="join-title">{CORP} // CREW ACCESS TERMINAL</h1>
  <p class="dim">AUTHORIZED PERSONNEL ONLY. TEMPORARY CONTRACTORS: RECORDS ARE NOT RETAINED.</p>
  <p class="dim">INTERCOM (VOICE/VIDEO): NOT INSTALLED ON THIS HULL. USE AN APPROVED EXTERNAL CHANNEL.</p>

  <form onsubmit={(e) => { e.preventDefault(); if (hullId.trim()) onBoard(); else onCommission(); }}>
    <label>CALLSIGN
      <input bind:value={name} maxlength={NAME_MAX} autocomplete="nickname" required disabled={busy} />
    </label>
    <label>PASSPHRASE <span class="dim">(MIN {PASSWORD_MIN})</span>
      <input bind:value={password} type="password" minlength={PASSWORD_MIN} autocomplete="off" required disabled={busy} />
    </label>
    {#if hulls.length}
      <div class="live" role="group" aria-label="Live hulls">
        <span class="dim">LIVE HULLS — PRESS TO FILL:</span>
        {#each hulls as h (h.hull)}
          <button type="button" class="tiny" onclick={() => { hullId = h.hull; }} disabled={busy}>{h.hull} · {h.crew} ABOARD</button>
        {/each}
      </div>
    {/if}
    <label>HULL CODE <span class="dim">(ABCD-1234 — LEAVE BLANK TO COMMISSION A NEW HULL)</span>
      <input class="code" value={hullId} oninput={(e) => { hullId = normalizeHullCode((e.currentTarget as HTMLInputElement).value); (e.currentTarget as HTMLInputElement).value = hullId; }}
        maxlength={HULL_CODE_LENGTH} autocomplete="off" autocapitalize="characters" spellcheck="false" inputmode="text" disabled={busy} placeholder="ABCD-1234" />
    </label>
    <div class="actions">
      <button type="button" onclick={onCommission} disabled={busy || !valid || !!hullId.trim()}>COMMISSION NEW HULL</button>
      <button type="submit" disabled={busy || !valid || !hullId.trim()}>BOARD HULL</button>
    </div>
  </form>

  {#if gate.config?.devHull !== false}
    <div class="dev">
      <button type="button" class="quick" onclick={onQuick} disabled={busy}>QUICK BOARD — DEV HULL {DEV_HULL_CODE}</button>
      <span class="dim">DEVELOPMENT ONLY. ONE PRESS, NO CODE, NO PASSPHRASE. CALLSIGN OPTIONAL.</span>
    </div>
  {/if}
  <p class="dim build">{BUILD_LABEL}</p>
  <p class="status" role="status" aria-live="polite">
    {#if busy}ESTABLISHING LINK…{:else if session.error}<span class="bad">{session.error}</span>{:else}&nbsp;{/if}
  </p>
</section>

<style>
  .join { max-width: 60ch; padding: 0.2rem 0; }
  form { display: grid; gap: 0.5rem; }
  label { display: grid; gap: 0.3rem; }
  input { width: 100%; }
  .code { font-size: 1.5em; letter-spacing: 0.2em; text-align: center; }
  .actions { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 0.4rem; }
  .status { min-height: 1.4em; margin-top: 0.5rem; }
  .build { font-size: 0.8em; margin-top: 0.5rem; user-select: all; }
  .dev { display: flex; flex-direction: column; gap: 0.2rem; margin-top: 0.6rem; padding-top: 0.5rem; border-top: 1px dashed var(--fg-dim); }
  .quick { border-color: var(--warn); color: var(--warn); min-height: 52px; }
  .live { display: flex; gap: 0.4rem; flex-wrap: wrap; align-items: center; }
  .tiny { min-height: 32px; padding: 0.2em 0.6em; font-size: 0.85em; }
</style>
